// Shared "create an initiative from a draft" logic — used by both the
// Initiatives page's own "+ New Initiative" flow and a Goal card's
// "+ Add Initiative" shortcut, so the two stay identical rather than
// slowly diverging.
import type { InitiativeDraft } from '../components/InitiativeForm';
import { DEFAULT_TASKS, stepDrafts } from './eventPlan';
import { createInitiativeBatch } from './db';
import type { AppSettings, ChangeEntry, EventTemplate, Position } from '../types';

export function chairPositionFor(committeeId: string, positions: Position[]): string | null {
  return positions.find((p) => p.committee === committeeId && p.lead)?.name ?? null;
}

/** Creates one initiative per district in the draft (a "rollout" when more
 *  than one), each with the same generated task list — position-name
 *  assignees resolve to whoever holds that seat in each district. */
export async function createInitiativesFromDraft(
  draft: InitiativeDraft,
  template: EventTemplate | null,
  settings: AppSettings,
  email: string,
): Promise<{ isRollout: boolean; districtCount: number }> {
  await createInitiativeBatch(initiativeItemsFromDraft(draft,template,settings,email));
  return {isRollout:draft.districtIds.length>1,districtCount:draft.districtIds.length};
}

/** Pure preparation shared by both UI entry points; persistence/refs/identity
 * and all authorization remain server-authoritative. */
export function initiativeItemsFromDraft(draft:InitiativeDraft,template:EventTemplate|null,settings:AppSettings,email:string) {
  const statusId = settings.taskStatuses[0]?.id ?? '';
  const positionNames = new Set(settings.positions.map((p) => p.name));
  const defaultAssignee = draft.leadPosition ?? chairPositionFor(draft.committee, settings.positions);
  const now = new Date().toISOString();
  const districtIds = draft.districtIds;
  const isRollout = districtIds.length > 1;

  const changeLog: ChangeEntry[] = draft.defaults
    .filter((c) => !c.include)
    .map((c) => ({
      by: email,
      at: now,
      action: `Dropped default task: ${DEFAULT_TASKS.find((x) => x.kind === c.kind)!.title}`,
      reason: c.reason.trim(),
    }));

  const tasksFor = (districtId: string) => {
    const base = {
      districtId,
      committee: draft.committee,
      status: statusId,
      dueDate: null as string | null,
      assignedContactIds: null,
      noteLog: [],
      createdBy: email,
    };
    const defaultTasks = draft.defaults
      .filter((c) => c.include)
      .map((c) => {
        const def = DEFAULT_TASKS.find((x) => x.kind === c.kind)!;
        return {
          ...base,
          title: def.title,
          description: def.description,
          category: def.category,
          offsetDays: null,
          defaultKind: def.kind,
          assignedPosition: defaultAssignee,
        };
      });
    const stepTasks = (template ? stepDrafts(template) : []).map((s) => ({
      ...base,
      title: s.title,
      description: s.description,
      category: s.category ?? null,
      offsetDays: s.offsetDays,
      defaultKind: null,
      assignedPosition:
        s.assignedPosition && positionNames.has(s.assignedPosition) ? s.assignedPosition : null,
    }));
    return [...defaultTasks, ...stepTasks];
  };

  return districtIds.map(districtId => ({
      initiative: {
        districtId,
        templateId: draft.templateId,
        name: draft.name,
        committee: draft.committee,
        strategicGoalId: draft.strategicGoalId,
        recurrence: template?.recurrence ?? 'once',
        origin: isRollout ? 'council' : 'district',
        eventDate: null,
        leadPosition: draft.leadPosition,
        leadContactIds: null,
        status: draft.status,
        notes: draft.notes,
        changeLog,
        createdBy: email,
        specializedContactAreaIds: draft.specializedContactAreaIds,
      },
      tasks: tasksFor(districtId),
  }));
}
